"use client"

import * as React from "react"
import { Link } from "react-router"
import { KeyMetrics } from "@/components/key-metrics"
import { Button } from "@/components/ui/button"
import { StatusBadge } from "@/components/ui/status-badge"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { DashboardSectionTitle } from "@/components/dashboard-section-heading"
import { evaluationDashboardKpiMetrics } from "@/lib/mock/evaluation-dashboard-kpi"
import { buildEvaluationWorkQueue } from "@/lib/mock/evaluation-work-queue"
import type { EvaluationTerm } from "@/lib/mock/evaluation-dashboard"
import { workQueueActionPath } from "@/lib/evaluation-routes"
import { useProduct } from "@/contexts/product-context"
import { EVAL_DASHBOARD_KPI_STRIP_CLASS } from "@/lib/evaluation-dashboard-a11y"

interface EvaluationDashboardActionViewProps {
  currentTerm?: EvaluationTerm
  lastTerm?: EvaluationTerm
}

const STATE_TONE: Record<
  ReturnType<typeof buildEvaluationWorkQueue>[number]["state"],
  "neutral" | "info" | "warning" | "success" | "danger"
> = {
  notSetup: "warning",
  draft: "neutral",
  scheduled: "info",
  live: "success",
  closed: "neutral",
  review: "info",
  attention: "warning",
}

export function EvaluationDashboardActionView({
  currentTerm,
  lastTerm,
}: EvaluationDashboardActionViewProps) {
  const { product } = useProduct()
  const metrics = React.useMemo(
    () => evaluationDashboardKpiMetrics(currentTerm, lastTerm),
    [currentTerm, lastTerm],
  )
  const queue = React.useMemo(() => buildEvaluationWorkQueue(), [])

  return (
    <div className="flex flex-col gap-10">
      <KeyMetrics
        variant="cards"
        size="md"
        metrics={metrics}
        showHeader={false}
        metricsSingleRow
        className={EVAL_DASHBOARD_KPI_STRIP_CLASS}
      />

      <section aria-labelledby="eval-action-queue-heading" className="flex flex-col gap-4">
        <DashboardSectionTitle as="h2" id="eval-action-queue-heading">
          Needs you
        </DashboardSectionTitle>

        {queue.length === 0 ? (
          <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center text-sm text-muted-foreground">
            No open actions. Evaluations are on track.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Term</TableHead>
                  <TableHead>Subject</TableHead>
                  <TableHead>State</TableHead>
                  <TableHead className="text-right">Response</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="w-[1%]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {queue.map(row => (
                  <TableRow key={row.id}>
                    <TableCell>
                      <div className="flex flex-col gap-0.5">
                        <span className="font-medium text-foreground">{row.termLabel}</span>
                        <span className="text-xs text-muted-foreground">{row.programName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-foreground">{row.subject}</TableCell>
                    <TableCell>
                      <StatusBadge
                        label={row.stateLabel}
                        tone={STATE_TONE[row.state]}
                        size="sm"
                      />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {row.responseRate != null ? `${row.responseRate}%` : "None"}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground whitespace-nowrap">
                      {row.dueLabel ?? "None"}
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end">
                        <Button size="sm" variant={row.priority <= 1 ? "default" : "outline"} asChild>
                          <Link to={workQueueActionPath(product, row)}>{row.actionLabel}</Link>
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>
    </div>
  )
}
